import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Plus, Edit, Trash2, Percent, DollarSign, Briefcase, Phone, User, ArrowDownFromLine, ArrowUpFromLine, Globe, GripVertical } from "lucide-react";

const SOURCE_TYPES = [
  { value: "referral", label: "Referral" },
  { value: "advertising", label: "Advertising" },
  { value: "partner", label: "Partner / Affiliate" },
  { value: "direct", label: "Direct / Walk-in" },
  { value: "online", label: "Online / Website" },
  { value: "other", label: "Other" },
];

const TIMEZONES = [
  { value: "", label: "Use account default" },
  { value: "America/New_York", label: "Eastern (ET)" },
  { value: "America/Chicago", label: "Central (CT)" },
  { value: "America/Denver", label: "Mountain (MT)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
  { value: "America/Anchorage", label: "Alaska (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii (HT)" },
  { value: "America/Phoenix", label: "Arizona (no DST)" },
  { value: "America/Toronto", label: "Eastern Canada" },
  { value: "America/Vancouver", label: "Pacific Canada" },
  { value: "Europe/London", label: "UK (GMT/BST)" },
  { value: "Europe/Paris", label: "Central Europe (CET)" },
  { value: "Europe/Berlin", label: "Germany (CET)" },
  { value: "Asia/Dubai", label: "Gulf (GST)" },
  { value: "Asia/Kolkata", label: "India (IST)" },
  { value: "Asia/Singapore", label: "Singapore (SGT)" },
  { value: "Australia/Sydney", label: "Sydney (AEST)" },
];

const TYPE_COLORS = {
  referral: "bg-blue-500/15 text-blue-400 border-blue-500/20",
  advertising: "bg-purple-500/15 text-purple-400 border-purple-500/20",
  partner: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  direct: "bg-amber-500/15 text-amber-400 border-amber-500/20",
  online: "bg-cyan-500/15 text-cyan-400 border-cyan-500/20",
  other: "bg-slate-500/15 text-slate-400 border-slate-500/20",
};

const TYPE_ACCENT = {
  referral: "border-l-blue-500",
  advertising: "border-l-purple-500",
  partner: "border-l-emerald-500",
  direct: "border-l-amber-500",
  online: "border-l-cyan-500",
  other: "border-l-slate-500",
};

const SortableSourceCard = ({ source, i, editSource, confirmDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: source.source_id });
  const typeColor = TYPE_COLORS[source.type] || TYPE_COLORS.other;
  const accentBorder = TYPE_ACCENT[source.type] || TYPE_ACCENT.other;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.65 : 1,
    zIndex: isDragging ? 20 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative rounded-xl bg-theme-surface border border-theme border-l-[3px] ${accentBorder} overflow-hidden transition-colors hover:border-white/20 ${isDragging ? "shadow-2xl ring-1 ring-[#FFCC00]/40" : ""}`}
      data-testid={`source-item-${i}`}
    >
      <div className="p-4">
        {/* Header Row */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              {...attributes}
              {...listeners}
              className="shrink-0 h-9 w-5 flex items-center justify-center text-muted-foreground/40 hover:text-foreground cursor-grab active:cursor-grabbing touch-none"
              title="Drag to reorder — this order is used in the job form"
              data-testid={`source-drag-handle-${i}`}
            >
              <GripVertical className="w-4 h-4" />
            </button>
            <div className="w-9 h-9 rounded-lg bg-[#0046BE]/15 flex items-center justify-center shrink-0">
              <Briefcase className="w-4.5 h-4.5 text-[#0046BE]" />
            </div>
            <div className="min-w-0">
              <h4 className="font-semibold text-foreground text-sm truncate">{source.name}</h4>
              <Badge className={`text-[10px] mt-0.5 border ${typeColor}`}>{source.type}</Badge>
            </div>
          </div>
          <div className="flex gap-0.5 shrink-0">
            <Button variant="ghost" size="icon" onClick={() => editSource(source)} className="h-7 w-7 text-muted-foreground hover:text-foreground">
              <Edit className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => confirmDelete("source", source.source_id, source.name)} className="h-7 w-7 text-red-400/70 hover:text-red-400">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#FFCC00]/10 border border-[#FFCC00]/20">
            {source.commission_type === "percentage"
              ? <Percent className="w-3 h-3 text-[#FFCC00]" />
              : <DollarSign className="w-3 h-3 text-[#FFCC00]" />}
            <span className="text-xs font-semibold text-[#FFCC00]">
              {source.commission_value}{source.commission_type === "percentage" ? "%" : ""}
            </span>
          </div>
          {source.extra_fee_enabled && source.extra_fee_per_job > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/15">
              <DollarSign className="w-3 h-3 text-amber-400" />
              <span className="text-[11px] font-medium text-amber-400">+${source.extra_fee_per_job}/job</span>
            </div>
          )}
          <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-muted/40">
            {(source.parts_deduction || "from_total") === "from_total"
              ? <ArrowDownFromLine className="w-3 h-3 text-muted-foreground" />
              : <ArrowUpFromLine className="w-3 h-3 text-muted-foreground" />}
            <span className="text-[11px] text-muted-foreground">
              {source.parts_deduction === "from_profit" ? "Parts from profit" : "Parts from total"}
            </span>
          </div>
          {source.override_business_name && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/15" title="Jobs from this source use its name as {{business_name}} in messages">
              <Briefcase className="w-3 h-3 text-emerald-400" />
              <span className="text-[11px] text-emerald-400">Brand in messages</span>
            </div>
          )}
          {source.timezone && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-blue-500/10 border border-blue-500/15">
              <Globe className="w-3 h-3 text-blue-400" />
              <span className="text-[11px] text-blue-400">{TIMEZONES.find(t => t.value === source.timezone)?.label || source.timezone}</span>
            </div>
          )}
        </div>

        {/* Contact Row */}
        {(source.contact_name || source.contact_phone) && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-2 border-t border-theme/50">
            {source.contact_name && (
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <User className="w-3 h-3" /> {source.contact_name}
              </span>
            )}
            {source.contact_phone && (
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Phone className="w-3 h-3" /> {source.contact_phone}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const JobSourcesManager = ({
  jobSources,
  sourceDialogOpen,
  setSourceDialogOpen,
  editingSource,
  sourceForm,
  setSourceForm,
  resetSourceForm,
  editSource,
  saveJobSource,
  confirmDelete,
  onReorder,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = jobSources.findIndex((s) => s.source_id === active.id);
    const newIndex = jobSources.findIndex((s) => s.source_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(jobSources, oldIndex, newIndex));
  };

  return (
    <Card className="glass-card border-theme" data-testid="job-sources-card">
      <CardHeader className="flex flex-row items-start sm:items-center justify-between gap-3 pb-4">
        <div className="min-w-0">
          <CardTitle className="text-foreground font-['Manrope'] text-lg">Job Sources</CardTitle>
          <CardDescription className="text-muted-foreground text-sm">
            Track where jobs come from and manage commissions. Drag cards to set the order they appear in the job form.
          </CardDescription>
        </div>
        <Dialog open={sourceDialogOpen} onOpenChange={(open) => { setSourceDialogOpen(open); if (!open) resetSourceForm(); }}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-[#0046BE] hover:bg-[#003DA5] text-white text-xs sm:text-sm rounded-full px-4 shrink-0" data-testid="add-source-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Source
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-background border-border max-w-md max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-foreground">{editingSource ? "Edit" : "Add"} Job Source</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 overflow-y-auto flex-1 pr-1">
              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm">Source Name *</Label>
                <Input
                  value={sourceForm.name}
                  onChange={(e) => setSourceForm({ ...sourceForm, name: e.target.value })}
                  className="input-glass"
                  placeholder="e.g., Google Ads, Partner Company"
                  data-testid="source-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Type</Label>
                  <Select value={sourceForm.type} onValueChange={(v) => setSourceForm({ ...sourceForm, type: v })}>
                    <SelectTrigger className="input-glass"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SOURCE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Commission Type</Label>
                  <Select value={sourceForm.commission_type} onValueChange={(v) => setSourceForm({ ...sourceForm, commission_type: v })}>
                    <SelectTrigger className="input-glass"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage (%)</SelectItem>
                      <SelectItem value="flat">Flat Fee ($)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm">Commission Value</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                    {sourceForm.commission_type === "percentage" ? "%" : "$"}
                  </span>
                  <Input
                    type="number"
                    value={sourceForm.commission_value}
                    onChange={(e) => setSourceForm({ ...sourceForm, commission_value: e.target.value })}
                    className="input-glass pl-8"
                    placeholder="0"
                    data-testid="source-commission-input"
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="p-3 rounded-lg border border-theme bg-theme-surface/50 space-y-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Contact Info</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs">Name</Label>
                    <Input
                      value={sourceForm.contact_name}
                      onChange={(e) => setSourceForm({ ...sourceForm, contact_name: e.target.value })}
                      className="input-glass h-9 text-sm"
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs">Phone</Label>
                    <Input
                      value={sourceForm.contact_phone}
                      onChange={(e) => setSourceForm({ ...sourceForm, contact_phone: e.target.value })}
                      className="input-glass h-9 text-sm"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm">Notes</Label>
                <Textarea
                  value={sourceForm.notes}
                  onChange={(e) => setSourceForm({ ...sourceForm, notes: e.target.value })}
                  className="input-glass min-h-[52px] text-sm"
                  placeholder="Additional notes..."
                />
              </div>

              {/* Timezone */}
              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5" /> Appointment Timezone
                </Label>
                <Select
                  value={sourceForm.timezone || "none"}
                  onValueChange={(v) => setSourceForm({ ...sourceForm, timezone: v === "none" ? "" : v })}
                >
                  <SelectTrigger className="input-glass" data-testid="source-timezone">
                    <SelectValue placeholder="Use account default" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map(tz => (
                      <SelectItem key={tz.value} value={tz.value || "none"}>{tz.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground/60 text-xs">
                  Reminders for jobs from this source will be scheduled using this timezone.
                </p>
              </div>

              {/* Business name override for templates */}
              <div className="p-3 rounded-lg border border-theme bg-theme-surface/50 space-y-1">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground font-medium">Use as business name in messages</p>
                    <p className="text-muted-foreground/50 text-xs">
                      {"{{business_name}}"} in SMS/email templates becomes this source's name for jobs from this source
                    </p>
                  </div>
                  <Switch
                    checked={sourceForm.override_business_name || false}
                    onCheckedChange={(checked) => setSourceForm({ ...sourceForm, override_business_name: checked })}
                    data-testid="source-brand-override-toggle"
                  />
                </div>
              </div>

              {/* Extra Fees */}
              <div className="p-3 rounded-lg border border-theme bg-theme-surface/50 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground font-medium">Extra Fee per Job</p>
                    <p className="text-muted-foreground/50 text-xs">Additional fee on each completed job</p>
                  </div>
                  <Switch
                    checked={sourceForm.extra_fee_enabled}
                    onCheckedChange={(checked) => setSourceForm({ ...sourceForm, extra_fee_enabled: checked })}
                    data-testid="extra-fee-toggle"
                  />
                </div>
                {sourceForm.extra_fee_enabled && (
                  <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs">Fee Amount ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={sourceForm.extra_fee_per_job}
                      onChange={(e) => setSourceForm({ ...sourceForm, extra_fee_per_job: parseFloat(e.target.value) || 0 })}
                      className="input-glass h-9 text-sm"
                      placeholder="0.00"
                      data-testid="extra-fee-amount"
                    />
                  </div>
                )}
              </div>

              {/* Parts Deduction */}
              <div className="p-3 rounded-lg border border-theme bg-theme-surface/50 space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Parts Cost Deduction</p>
                  <p className="text-muted-foreground/50 text-xs">How parts cost affects commission</p>
                </div>
                <Select
                  value={sourceForm.parts_deduction || "from_total"}
                  onValueChange={(val) => setSourceForm({ ...sourceForm, parts_deduction: val })}
                >
                  <SelectTrigger className="input-glass h-9 text-sm" data-testid="parts-deduction-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-theme">
                    <SelectItem value="from_total">Deduct from job total (before commission)</SelectItem>
                    <SelectItem value="from_profit">Deduct from company profit (after commission)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground/50 text-[11px] leading-relaxed">
                  {(sourceForm.parts_deduction || "from_total") === "from_total"
                    ? "Parts subtracted from total before calculating commission."
                    : "Commission on full total. Parts subtracted from company profit."}
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-2 sticky bottom-0 bg-background pb-1">
                <Button variant="ghost" onClick={() => setSourceDialogOpen(false)} className="text-muted-foreground">Cancel</Button>
                <Button onClick={saveJobSource} className="btn-primary" data-testid="save-source-btn">
                  {editingSource ? "Update" : "Create"} Source
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent className="pt-0">
        {jobSources.length > 0 ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={jobSources.map((s) => s.source_id)} strategy={rectSortingStrategy}>
              <div className="grid gap-3 sm:grid-cols-2">
                {jobSources.map((source, i) => (
                  <SortableSourceCard
                    key={source.source_id}
                    source={source}
                    i={i}
                    editSource={editSource}
                    confirmDelete={confirmDelete}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="text-center py-12 px-4">
            <div className="w-14 h-14 rounded-2xl bg-[#0046BE]/10 flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-7 h-7 text-[#0046BE]/40" />
            </div>
            <p className="text-foreground font-medium">No job sources yet</p>
            <p className="text-muted-foreground/60 text-sm mt-1 max-w-[260px] mx-auto">Add sources to track where your jobs come from and manage commissions</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default JobSourcesManager;
